import { AuthStorage } from "utils/auth-storage";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { Button, message, Space } from "antd";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Tooltip } from "antd";
import { ExclamationCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import { WarehouseServices } from "apis/Services/WarehouseService";
import { useParams } from "react-router-dom";

ModuleRegistry.registerModules([AllCommunityModule]);

const modules = [AllCommunityModule];

const normalizeImportedInventoryRow = (row = {}) => ({
  ...row,
  materialName: row.materialName ?? row["Material Name"] ?? "",
  sku: row.sku ?? row.SKU ?? "",
  itemVendor: row.itemVendor ?? row.vendor ?? row.Vendor ?? "",
  category: row.category ?? row.Category ?? "",
  subcategory: row.subcategory ?? row.Subcategory ?? "",
  description: row.description ?? row.Description ?? "",
  unitPrice: row.unitPrice ?? row["Unit Price"] ?? "",
  unit:
    row.unit !== undefined && row.unit !== null
      ? String(row.unit).trim()
      : row.Unit !== undefined && row.Unit !== null
        ? String(row.Unit).trim()
        : "",
  currentStock: row.currentStock ?? row["Current Stock"] ?? "",
  minStock: row.minStock ?? row["Min Stock"] ?? "",
  maxStock: row.maxStock ?? row["Max Stock"] ?? "",
  storageLocation: row.storageLocation ?? row["Storage Location"] ?? "",
  itemBay: row.itemBay ?? row.Bay ?? "",
  itemShelf: row.itemShelf ?? row.Shelf ?? "",
  itemRow: row.itemRow ?? row.Row ?? "",
});

const normalizeImportedInventoryRows = (rows) =>
  Array.isArray(rows) ? rows.map(normalizeImportedInventoryRow) : [];

function ReviewFile({
  rejectedData = [],
  acceptedData = [],
  handleCancel,
  setAcceptedData,
  setRejectedData,
  categoryOptions,
  subcategoryOptions,
  locationsOptions,
  unitsOptions,
  setShouldReload = () => {},
}) {
  const { company_id } = useParams();
  const [selectedView, setSelectedView] = useState("clean");
  const [selectedRows, setSelectedRows] = useState([]);
  const [importing, setImporting] = useState();
  const gridRef = useRef(null);

  const allData = useMemo(
    () => [
      ...normalizeImportedInventoryRows(acceptedData),
      ...normalizeImportedInventoryRows(rejectedData),
    ],
    [acceptedData, rejectedData],
  );

  const tableData = useMemo(() => {
    if (selectedView === "clean") {
      return normalizeImportedInventoryRows(acceptedData);
    }
    if (selectedView === "issues") {
      return normalizeImportedInventoryRows(rejectedData);
    }
    return allData;
  }, [selectedView, acceptedData, rejectedData, allData]);

  // Helper function to validate stock values
  const validateStock = (params, field) => {
    const minStock = parseFloat(params.data?.minStock);
    const maxStock = parseFloat(params.data?.maxStock);
    const currentStock = parseFloat(params.data?.currentStock);

    if (field === "minStock") {
      if (!params.value) {
        return { isValid: false, message: "This field is required" };
      }
      if (!isNaN(minStock) && !isNaN(maxStock) && minStock > maxStock) {
        return {
          isValid: false,
          message: "Min stock cannot be greater than max stock",
        };
      }
    }

    if (field === "maxStock") {
      if (!params.value) {
        return { isValid: false, message: "This field is required" };
      }
      if (!isNaN(minStock) && !isNaN(maxStock) && maxStock < minStock) {
        return {
          isValid: false,
          message: "Max stock cannot be less than min stock",
        };
      }
      if (!isNaN(currentStock) && !isNaN(maxStock) && currentStock > maxStock) {
        return {
          isValid: false,
          message: "Current stock cannot exceed max stock",
        };
      }
    }

    if (field === "currentStock") {
      if (!params.value) {
        return { isValid: false, message: "This field is required" };
      }
      if (!isNaN(currentStock) && !isNaN(maxStock) && currentStock > maxStock) {
        return {
          isValid: false,
          message: "Current stock cannot exceed max stock",
        };
      }
    }

    return { isValid: true, message: null };
  };

  // Helper function to validate unit price
  const validateUnitPrice = (params) => {
    if (
      params.value === null ||
      params.value === undefined ||
      params.value === ""
    ) {
      return { isValid: false, message: "This field is required" };
    }
    const numericValue = Number(params.value);
    if (isNaN(numericValue)) {
      return { isValid: false, message: "Unit price must be a number" };
    }
    return { isValid: true, message: null };
  };

  // Helper function to validate category
  const validateCategory = (params) => {
    if (!params.value) {
      return { isValid: true, message: null };
    }
    const isValid = categoryOptions.some(
      (option) => option.name === params.value,
    );
    return {
      isValid,
      message: isValid ? null : "Category does not exist in inventory settings",
    };
  };

  // Helper function to validate subcategory
  const validateSubcategory = (params) => {
    if (!params.value) {
      return { isValid: true, message: null };
    }
    const isValid = subcategoryOptions.some(
      (option) => option.name === params.value,
    );
    return {
      isValid,
      message: isValid
        ? null
        : "Subcategory does not exist in inventory settings",
    };
  };

  // Helper function to validate unit
  const validateUnit = (params) => {
    const normalizedValue = String(params.value ?? "").trim();

    if (!normalizedValue) {
      return { isValid: false, message: "This field is required" };
    }

    if (!Number.isNaN(Number(normalizedValue))) {
      return {
        isValid: false,
        message: "Unit must be text, not a numeric value",
      };
    }

    const isValid = unitsOptions.some(
      (option) =>
        String(option?.name ?? "")
          .trim()
          .toLowerCase() === normalizedValue.toLowerCase(),
    );
    return {
      isValid,
      message: isValid ? null : "Unit does not exist in inventory settings",
    };
  };

  // Helper function to validate storage location
  const validateLocation = (params) => {
    if (!params.value) {
      return { isValid: false, message: "This field is required" };
    }
    const isValid = locationsOptions.some(
      (option) => option.name === params.value,
    );
    return {
      isValid,
      message: isValid
        ? null
        : "Storage location does not exist in inventory settings",
    };
  };

  // helper to produce a column def with required-cell rules + tooltip
  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        pinned: "left",
        width: 100,
        suppressMovable: true,
        sortable: false,
        filter: false,
        lockPosition: "left",
        resizable: false,
        field: "",
        cellRenderer: (params) => {
          const lineNumber = params.node.rowIndex + 1;
          const errors = params.data?.errorMessage || [];
          const hasErrors = Array.isArray(errors) && errors.length > 0;

          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                height: "100%",
              }}
            >
              <span>{lineNumber}</span>
              {/* {hasErrors && (
                <Tooltip
                  title={
                    <div>
                      {errors.map((err, idx) => (
                        <div key={idx}>• {err}</div>
                      ))}
                    </div>
                  }
                >
                  <ExclamationCircleOutlined
                    style={{
                      color: "#ff4d4f",
                      fontSize: 16,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              )} */}
            </div>
          );
        },
      },
      {
        headerName: "Material Name",
        field: "materialName",
        editable: true,
        minWidth: 160,
        cellStyle: (params) =>
          !params.value
            ? { backgroundColor: "#fff2f0", color: "red" }
            : { backgroundColor: "", color: "black" },
        tooltipValueGetter: (params) =>
          !params.value ? "This field is required" : null,
      },
      {
        headerName: "SKU",
        field: "sku",
        editable: true,
        minWidth: 160,
      },
      {
        headerName: "Vendor",
        field: "itemVendor",
        editable: true,
        minWidth: 180,
      },
      {
        headerName: "Category",
        field: "category",
        editable: true,
        minWidth: 160,
        tooltipValueGetter: (params) => {
          const validation = validateCategory(params);
          return validation.message;
        },
        cellStyle: (params) => {
          const validation = validateCategory(params);
          return !validation.isValid
            ? { backgroundColor: "#fff2f0", color: "red" }
            : { backgroundColor: "", color: "black" };
        },
      },
      {
        headerName: "Subcategory",
        field: "subcategory",
        editable: true,
        minWidth: 160,
        tooltipValueGetter: (params) => {
          const validation = validateSubcategory(params);
          return validation.message;
        },
        cellStyle: (params) => {
          const validation = validateSubcategory(params);
          return !validation.isValid
            ? { backgroundColor: "#fff2f0", color: "red" }
            : { backgroundColor: "", color: "black" };
        },
      },
      {
        headerName: "Description",
        field: "description",
        editable: true,
        minWidth: 160,
      },
      {
        headerName: "Unit Price",
        field: "unitPrice",
        editable: true,
        minWidth: 160,
        tooltipValueGetter: (params) => {
          const validation = validateUnitPrice(params);
          return validation.message;
        },
        cellStyle: (params) => {
          const validation = validateUnitPrice(params);
          return !validation.isValid
            ? { backgroundColor: "#fff2f0", color: "red" }
            : { backgroundColor: "", color: "black" };
        },
        valueFormatter: (params) =>
          params.value !== null &&
          params.value !== undefined &&
          params.value !== ""
            ? `$${params.value}`
            : "",
      },
      {
        headerName: "Unit",
        field: "unit",
        editable: true,
        cellDataType: false,
        minWidth: 160,
        valueParser: (params) => String(params.newValue ?? "").trim(),
        tooltipValueGetter: (params) => {
          const validation = validateUnit(params);
          return validation.message;
        },
        cellStyle: (params) => {
          const validation = validateUnit(params);
          return !validation.isValid
            ? { backgroundColor: "#fff2f0", color: "red" }
            : { backgroundColor: "", color: "black" };
        },
      },
      {
        headerName: "Current Stock",
        field: "currentStock",
        editable: true,
        minWidth: 160,
        tooltipValueGetter: (params) => {
          const validation = validateStock(params, "currentStock");
          return validation.message;
        },
        cellStyle: (params) => {
          const validation = validateStock(params, "currentStock");
          return !validation.isValid
            ? { backgroundColor: "#fff2f0", color: "red" }
            : { backgroundColor: "", color: "black" };
        },
      },
      {
        headerName: "Min Stock",
        field: "minStock",
        editable: true,
        minWidth: 160,
        tooltipValueGetter: (params) => {
          const validation = validateStock(params, "minStock");
          return validation.message;
        },
        cellStyle: (params) => {
          const validation = validateStock(params, "minStock");
          return !validation.isValid
            ? { backgroundColor: "#fff2f0", color: "red" }
            : { backgroundColor: "", color: "black" };
        },
      },
      {
        headerName: "Max Stock",
        field: "maxStock",
        editable: true,
        minWidth: 160,
        tooltipValueGetter: (params) => {
          const validation = validateStock(params, "maxStock");
          return validation.message;
        },
        cellStyle: (params) => {
          const validation = validateStock(params, "maxStock");
          return !validation.isValid
            ? { backgroundColor: "#fff2f0", color: "red" }
            : { backgroundColor: "", color: "black" };
        },
      },
      {
        headerName: "Storage Location",
        field: "storageLocation",
        editable: true,
        minWidth: 220,
        tooltipValueGetter: (params) => {
          const validation = validateLocation(params);
          return validation.message;
        },
        cellStyle: (params) => {
          const validation = validateLocation(params);
          return !validation.isValid
            ? { backgroundColor: "#fff2f0", color: "red" }
            : { backgroundColor: "", color: "black" };
        },
      },
      {
        headerName: "Bay",
        field: "itemBay",
        editable: true,
        minWidth: 160,
        valueParser: (params) => params.newValue,
      },
      {
        headerName: "Shelf",
        field: "itemShelf",
        editable: true,
        minWidth: 160,
        valueParser: (params) => params.newValue,
      },
      {
        headerName: "Row",
        field: "itemRow",
        editable: true,
        minWidth: 160,
        valueParser: (params) => params.newValue,
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      // optional: small padding so tooltips don't overlap
      tooltipComponentParams: { color: "#ffffff" },
    }),
    [],
  );

  // refresh cells when data/view changes so styles/tooltips are re-applied
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshCells({ force: true });
    }
  }, [tableData, selectedView]);

  const handleImport = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.stopEditing();
    }

    setImporting(true);
    const code = AuthStorage.getItem("token");
    const email = AuthStorage.getItem("email");

    const updatedData = [];
    gridRef.current.api.forEachNode((node) => {
      updatedData.push(node.data);
    });

    const mappedData = updatedData.map((item) => ({
      "Material Name": item.materialName || "",
      SKU: item.sku || "",
      Vendor: item.itemVendor || "",
      Category: item.category || "",
      Subcategory: item.subcategory || "",
      Description: item.description || "",
      "Unit Price": item.unitPrice || "",
      Unit: item.unit || "",
      "Current Stock": item.currentStock || "",
      "Min Stock": item.minStock || "",
      "Max Stock": item.maxStock || "",
      "Storage Location": item.storageLocation || "",
      Bay: item.itemBay || "",
      Shelf: item.itemShelf || "",
      Row: item.itemRow || "",
    }));

    const ReqObj = {
      company: company_id,
      code,
      loginEmail: email,
      saveFlag: "2",
      inventoryRows: mappedData,
    };

    WarehouseServices.uploadWarehouseItems(ReqObj)
      .then((res) => {
        if (res.invalidItems.length > 0) {
          const acceptedCount = updatedData.length - res.invalidItems.length;
          message.warning(
            `${acceptedCount} items were imported successfully. ${res.invalidItems.length} items contain errors and were not imported. Please review them below.`,
            6,
          );
          setShouldReload(true);
          setSelectedView("issues");
        } else {
          message.success("All rows imported successfully!");
          handleCancel();
          setShouldReload(true);
        }
        setAcceptedData([]);
        setRejectedData(normalizeImportedInventoryRows(res.invalidItems));
      })
      .catch((err) => {
        console.error(err);
        message.error("Failed to import data. Please try again.");
      })
      .finally(() => {
        setImporting(null);
      });
  };

  const onSelectionChanged = () => {
    const selected = gridRef.current.api.getSelectedRows();
    setSelectedRows(selected);
  };

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button
          shape="round"
          size="large"
          type={selectedView === "all" ? "primary" : "default"}
          onClick={() => setSelectedView("all")}
        >
          All Rows{" "}
          <span
            style={{
              backgroundColor: "#f0f0f0",
              color: "#333",
              borderRadius: "50%",
              padding: "2px 8px",
              marginLeft: 0,
              fontSize: 13,
              fontWeight: 500,
              lineHeight: "20px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 22,
            }}
          >
            {allData.length}
          </span>
        </Button>

        <Button
          shape="round"
          size="large"
          type={selectedView === "clean" ? "primary" : "default"}
          onClick={() => setSelectedView("clean")}
        >
          Clean Rows{" "}
          <span
            style={{
              backgroundColor: "#f0f0f0",
              color: "#333",
              borderRadius: "50%",
              padding: "2px 8px",
              marginLeft: 0,
              fontSize: 13,
              fontWeight: 500,
              lineHeight: "20px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 22,
            }}
          >
            {acceptedData.length}
          </span>
        </Button>
        <Button
          shape="round"
          size="large"
          type={selectedView === "issues" ? "primary" : "default"}
          onClick={() => setSelectedView("issues")}
        >
          Rows with Issues{" "}
          <span
            style={{
              backgroundColor: "#f0f0f0",
              color: "#333",
              borderRadius: "50%",
              padding: "2px 8px",
              marginLeft: 0,
              fontSize: 13,
              fontWeight: 500,
              lineHeight: "20px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 22,
            }}
          >
            {rejectedData.length}
          </span>
        </Button>
      </Space>

      <div
        className="ag-theme-alpine"
        style={{
          height: 500,
          width: "100%",
          marginBottom: 16,
        }}
      >
        <AgGridReact
          ref={gridRef}
          modules={modules}
          rowData={tableData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          suppressDragLeaveHidesColumns={true}
          animateRows
          editType="singleCell"
          singleClickEdit={false}
          onSelectionChanged={onSelectionChanged}
          onCellClicked={(params) => {
            if (params.colDef.field === "") {
              const isSelected = params.node.isSelected();
              params.node.setSelected(!isSelected);
            }
          }}
          onCellValueChanged={(params) => {
            if (
              params.colDef.field === "minStock" ||
              params.colDef.field === "maxStock" ||
              params.colDef.field === "currentStock"
            ) {
              gridRef.current.api.refreshCells({
                rowNodes: [params.node],
                columns: ["minStock", "maxStock", "currentStock"],
                force: true,
              });
            } else {
              gridRef.current.api.refreshCells({
                rowNodes: [params.node],
                force: true,
              });
            }
          }}
          tooltipShowDelay={100}
        />
      </div>

      <div className=" d-flex justify-content-between align-items-center">
        <div>
          {selectedRows.length > 0 && (
            <Button
              type="default"
              danger
              size="large"
              shape="round"
              onClick={() => {
                const selectedIds = new Set(selectedRows.map((r) => r.lineCnt));
                let newAccepted = [...acceptedData];
                let newRejected = [...rejectedData];

                if (selectedView === "clean") {
                  newAccepted = acceptedData.filter(
                    (row) => !selectedIds.has(row.lineCnt),
                  );
                } else if (selectedView === "issues") {
                  newRejected = rejectedData.filter(
                    (row) => !selectedIds.has(row.lineCnt),
                  );
                } else {
                  // For "all", remove from both
                  newAccepted = acceptedData.filter(
                    (row) => !selectedIds.has(row.lineCnt),
                  );
                  newRejected = rejectedData.filter(
                    (row) => !selectedIds.has(row.lineCnt),
                  );
                }

                setAcceptedData(newAccepted);
                setRejectedData(newRejected);
                setSelectedRows([]);

                message.success("Selected rows deleted successfully.");
              }}
            >
              <DeleteOutlined />
              Delete ({selectedRows.length}) selected row
            </Button>
          )}
        </div>

        <Button
          type="primary"
          size="large"
          // shape="round"
          loading={importing}
          onClick={handleImport}
        >
          Import
        </Button>
      </div>
    </div>
  );
}

export default ReviewFile;
